import React, { Component } from 'react';
import moment from 'moment';
import { Collapse } from 'react-collapse';
import { NavLink } from 'react-router-dom';
import { Modal } from 'react-bootstrap';

import Spinner from '../generics/Spinner';
import { SCAN_SCANNING, SCAN_OK, SCAN_ERROR } from '../generics/QRScanner';
import AddInventoryWidget from '../generics/AddInventoryWidget';

import { addItemToPackage, deleteItemFromPackage } from '../../store/shipments/api';

import { groupItemsByStation, groupInventoryByItem } from "../../utils/Helpers";
import Alert from '../../utils/Alert';

const calculateInventoryCount = (orderItemIdList, orderItems, itemInventories) => {
  const amountByGrade = {
    null: 0,
  };
  // Initialize counter for amount by grade.
  // If item is not ordered by grade, only key will be null
  orderItemIdList.forEach((item) => {
    amountByGrade[orderItems[item].grade] = 0;
  });
  itemInventories.forEach((inv) => {
    let invGrade = null;
    if (inv.extra_info && inv.extra_info.grade) {
      invGrade = inv.extra_info.grade.grade;
    }
    if (amountByGrade[invGrade] !== undefined) {
      /*
        Two cases:
        1. If the inventory has Grade, and the Order requests that grade, we add
           it to that counter
        2. If the inventory doesn't have grade, we add it to the general counter
      */
      amountByGrade[invGrade] += 1;
    } else {
      // If the inventory has a grade, but not one the order asks for,
      // we add it to the general counter
      amountByGrade[null] += 1;
    }
  });
  return amountByGrade;
};

const InventoryCell = ({ orderItems, invCount, itemInventories }) => {
  const orderItemIdList = orderItems ? Object.keys(orderItems) : [];
  if (
    orderItems === undefined ||
    (orderItems[orderItemIdList[0]].presentation && orderItems[orderItemIdList[0]].presentation === "weight")
  ) {
    // Not an order shipment, or not tracking by inventory count
    return <td className="hidden-xs-down">{invCount}</td>;
  }

  const amountByGrade = calculateInventoryCount(orderItemIdList, orderItems, itemInventories);

  return (
    <td className="hidden-xs-down">
      {orderItemIdList.map((orderItemId) => {
        const item = orderItems[orderItemId];
        return (
          <p className="m-0" key={orderItemId}>
            {item.grade && `${item.grade}: `} {amountByGrade[item.grade]} / {item.amount}
          </p>
        );
      })}
    </td>
  );
};

const WeightCell = ({orderItems, itemInventories}) => {
  const orderItemIdList = orderItems ? Object.keys(orderItems) : [];
  const itemMeasureUnit = itemInventories[0].item.measure_unit ? itemInventories[0].item.measure_unit : 'kg';

  if (orderItems === undefined) {
    const itemTotalWeight = itemInventories.reduce((prevValue, inv) => {
      return prevValue + inv.weight;
    }, 0).toFixed(2);
    // Not an order shipment, or not tracking by weight
    return (
      <td className="hidden-xs-down">
        {itemTotalWeight}
        <span className="badge badge-pill badge-info ml-025">{itemMeasureUnit}</span>
      </td>
    );
  }
  const trackingByGrade = (
    orderItems[orderItemIdList[0]].presentation &&
    orderItems[orderItemIdList[0]].presentation === 'weight'
  );
  const amountByGrade = {
    null: 0,
  }
  // Initialize counter for amount by grade.
  // If item is not ordered by grade, only key will be null
  orderItemIdList.forEach((item) => {
    amountByGrade[orderItems[item].grade] = 0;
  })
  itemInventories.forEach((inv) => {
    let invGrade = null;
    if (inv.extra_info && inv.extra_info.grade) {
      invGrade = inv.extra_info.grade.grade;
    }
    if (amountByGrade[invGrade] !== undefined) {
      /*
        Two cases:
        1. If the inventory has Grade, and the Order requests that grade, we add
           it to that counter
        2. If the inventory doesn't have grade, and the item does not ask for it
      */
      const newValue = amountByGrade[invGrade] + inv.weight;
      amountByGrade[invGrade] = newValue;
    } else {
      // If the item does not track by grade, we add it to the general counter
      const newValue = amountByGrade[null] + inv.weight;
      amountByGrade[null] = newValue;
    }
  });

  return (
    <td className="hidden-xs-down">
      {orderItemIdList.map((orderItemId) => {
        const item = orderItems[orderItemId];
        return (
          <p className="m-0" key={orderItemId}>
            {item.grade && `${item.grade}: `}
            {amountByGrade[item.grade].toFixed(2)}
            {trackingByGrade && ` / ${item.amount}`}
            <span className="badge badge-pill badge-info ml-025">{itemMeasureUnit}</span>
          </p>
        )
      })}
    </td>
  )
}

const BackOrderCell = ({
  orderItems,
  handleBackOrderChange,
  handleCompletedNotBackOrderChange,
  openChangeSubstituteItemModal,
  itemInventories,
  stationName
}) => {
  const orderItemIdList = orderItems ? Object.keys(orderItems) : [];

  const amountByGrade = calculateInventoryCount(orderItemIdList, orderItems, itemInventories);

  if (orderItemIdList.length === 0) {
    return <td />;
  } else if (orderItemIdList.length === 1) {
    const item = orderItems[orderItemIdList[0]];
    const quantityLeftToSubstitute = item.amount - amountByGrade[null];
    return (
      <td>
        <div className="back-order-options">
          <div>
            <input
              type="checkbox"
              checked={item.in_back_order}
              onChange={(event) => handleBackOrderChange(item.id, item.item.id, event.target.checked)}
            />
            <span className="ml-1 mr-0">Back order</span>
          </div>
          {stationName === "Butcher" && quantityLeftToSubstitute > 0 && (
            <i
              className="ml-1 mr-0 cursor-pointer fa fa-edit"
              onClick={() => openChangeSubstituteItemModal(item.item, item.grade, quantityLeftToSubstitute)}
            />
          )}
        </div>
        {stationName === "Butcher" && quantityLeftToSubstitute > 0 && item.presentation === "piece" && (
          <div className="flex-align-center">
            <input
              type="checkbox"
              checked={item.completed_not_back_order}
              onChange={(event) => handleCompletedNotBackOrderChange(item.id, item.item.id, event.target.checked)}
            />
            <span className="ml-1 mr-0">Completed (Not back order)</span>
          </div>
        )}
      </td>
    );
  }

  return (
    <td>
      {orderItemIdList.map((orderItemId) => {
        const item = orderItems[orderItemId];
        const quantityLeftToSubstitute = item.amount - amountByGrade[item.grade];
        return (
          <div className="m-0 mb-1" key={orderItemId}>
            <label className="mr-025">{item.grade}:</label>
            <div class="back-order-options">
              <div>
                <input
                  type="checkbox"
                  checked={item.in_back_order}
                  onChange={(event) => handleBackOrderChange(item.id, item.item.id, event.target.checked)}
                />
                <span className="ml-1 mr-0">Back Order</span>
              </div>
              {stationName === "Butcher" && quantityLeftToSubstitute > 0 && (
                <i
                  className="ml-1 mr-0 cursor-pointer fa fa-edit"
                  onClick={() => openChangeSubstituteItemModal(item.item, item.grade, quantityLeftToSubstitute)}
                />
              )}
            </div>
            {stationName === "Butcher" && quantityLeftToSubstitute > 0 && item.presentation === "piece" && amountByGrade[item.grade] > 0 && (
              <div className="flex-align-center">
                <input
                  type="checkbox"
                  checked={item.completed_not_back_order}
                  onChange={(event) => handleCompletedNotBackOrderChange(item.id, item.item.id, event.target.checked)}
                />
                <span className="ml-1 mr-0">Completed (Not back order)</span>
              </div>
            )}
          </div>
        );
      })}
    </td>
  );
};

const PackageItemRow = (props) => {
  return (
    <tr>
      <td>
        <NavLink className="ml-05" to={`/inventory/${props.inventory.id}`}>
          {props.inventory.id}
        </NavLink>
      </td>
      <td>{props.inventory.item.name}</td>
      <td className="hidden-xs-down">1</td>
      <td className="hidden-xs-down">{props.inventory.brand.brand_name}</td>
      <td className="hidden-xs-down">{moment(props.inventory.packing_date).format('L')}</td>
      <td className="hidden-xs-down row border-0 b-t-1 m-0">
        <span>{props.inventory.weight}</span>
        <span className="measureUnit">
          <span className="badge badge-pill badge-info ml-025"> {props.inventory.item.measure_unit ? props.inventory.item.measure_unit : 'kg'}</span>
        </span>
      </td>
      <td className="hidden-xs-down">{props.inventory.number_items}</td>
      <td>
        <span className="btn btn-danger mr-0 p-05" onClick={() => props.onDelete(props.inventory.id)}><i className="fa fa-trash"></i></span>
      </td>
    </tr>
  );
};

export default class Package extends Component {

  // ---------------------------------------------------------------------------
  // Initialization
  // ---------------------------------------------------------------------------
  constructor(props){
    super(props);

    this.state = {
      inventoryId: '',
      scannedItems: [],
      scanResult: undefined,
      scanStatus: SCAN_SCANNING,
      collapseOpened: true,
      loading: false,
      itemsAccordionStatus: {},
      itemsByStation: {},
      orderItems: {},
      fifoWarning: null,      // stores 409 violation data
      pendingFifoPack: null,  // stores { packageId, inventoryId, format, times } for retry
    };

    this.alertRef = React.createRef();
  }

  componentDidMount() {
    // Create the initial grouping and accordion state
    this.updateItemMaps();
  }

  componentDidUpdate(prevProps) {
    // When the Order is loaded, get the items that are included in this package.
    if (!this.props.order.loading && prevProps.order.loading) {
      this.updateItemMaps();
    }
  }

  updateItemMaps = () => {
    const groupedItems = groupItemsByStation(this.props.package.items);
    const updatedAccordionState = {}
    Object.keys(groupedItems).forEach((station) => {
      const inventoryByItem = groupInventoryByItem(groupedItems[station]);
      groupedItems[station] = inventoryByItem;
      Object.keys(inventoryByItem).forEach((item) => {
        if (this.state.itemsAccordionStatus[item] === undefined) {
          updatedAccordionState[item] = false;
        } else {
          updatedAccordionState[item] = this.state.itemsAccordionStatus[item];
        }
      })
    });

    const {
      package: pkg,
      order,
      isOrderShipment
    } = this.props;
    const orderItems = {};

    if (isOrderShipment && order && order.order) {
      pkg.items.forEach((item) => {
        order.order.items.forEach((orderItem) => {
          if (orderItem.item.id === item.item.id) {
            if (!orderItems[item.item.id]) {
              orderItems[item.item.id] = {};
            }
            orderItems[item.item.id][orderItem.id] = Object.assign({}, orderItem);
          }
        });
      })
    }

    this.setState({
      itemsByStation: groupedItems,
      itemsAccordionStatus: updatedAccordionState,
      orderItems,
    });
  };

  toggleItemRows = (itemName) => {
    this.setState((prevState) => {
      return {
        itemsAccordionStatus: {
          ...prevState.itemsAccordionStatus,
          [itemName]: !prevState.itemsAccordionStatus[itemName],
        }
      }
    })
  };

  // ---------------------------------------------------------------------------
  // Collapse
  // ---------------------------------------------------------------------------
  toggleCollapse = () => {
    this.setState({collapseOpened: !this.state.collapseOpened});
  };

  // ---------------------------------------------------------------------------
  // Package
  // ---------------------------------------------------------------------------
  onRemovePackage = () => {
    this.props.onRemove(this.props.package.id);
  };

  // ---------------------------------------------------------------------------
  // Items
  // ---------------------------------------------------------------------------
  onItemIdChange = (event) => {
    this.setState({ inventoryId: event.target.value });
  };

  handleAddInventory = (times) => {
    const packageId = this.props.package.id;
    const inventoryId = this.state.inventoryId;
    this.setState({loading: true, inventoryId: ''}, () => {
      addItemToPackage(packageId, inventoryId, null, times).then((resp) => {
        const orderItemsForItem = this.state.orderItems[resp.data[0].item.id];
        const orderItemIdList = orderItemsForItem ? Object.keys(orderItemsForItem) : [];
        this.props.onAddItem(packageId, resp.data, orderItemIdList);
        this.setState({
          loading: false,
          inventoryId: ''
        }, () => {
          const msg = resp.data.length === 1 ? `Inventory #${resp.data[0].id}` : `Inventories`
          this.alertRef.current.sendAlert(`${msg} successfully added to package ${packageId}`, "success");
        });
        this.updateItemMaps();
      }, (error) => {
        if (this.handleFifoError(error, packageId, inventoryId, null, times)) return;
        this.setState({
          loading: false,
          inventoryId: ''
        }, () => {
          this.alertRef.current.sendAlert(error.response.data);
        });
      });
    });
  };

  handleDeleteInventory = (inventoryId) => {
    this.setState({loading: true});
    let packageId = this.props.package.id;
    deleteItemFromPackage(packageId, inventoryId)
    .then((resp) => {
      this.props.onDeleteItem(packageId, inventoryId);
      this.setState({loading: false}, () => {
        this.alertRef.current.sendAlert(
          `Item deleted from package ${packageId}`,
          "success"
        );
      });
      this.updateItemMaps();
    }).catch((err) => {
      this.setState({loading: false}, () => {
        if (err.response) {
          this.alertRef.current.sendAlert(err.response.data);
        } else {
          this.alertRef.current.sendAlert('Cannot delete item from package');
        }
      });
    });
  };

  // ---------------------------------------------------------------------------
  // Scanned Items
  // ---------------------------------------------------------------------------

  handleScannedItem = (scan, format, times) => {
    const packageId = this.props.package.id;
    const scannedId = scan;
    this.setState({
      scanResult: `Scanned item #${scannedId}`,
      scanStatus: SCAN_OK,
    }, () => {
      addItemToPackage(packageId, scannedId, format, times).then((resp) => {
        const orderItemsForItem = this.state.orderItems[resp.data[0].item.id];
        const orderItemIdList = orderItemsForItem ? Object.keys(orderItemsForItem) : [];
        this.props.onAddItem(packageId, resp.data, orderItemIdList);
        this.updateItemMaps();
        setTimeout(this.cleanMessage, 1500);
        this.alertRef.current.sendAlert(
          `Inventory #${scannedId} successfully added to package ${packageId}`,
          "success"
        );
      }).catch((err) => {
        if (this.handleFifoError(err, packageId, scannedId, format, times)) return;
        this.setState({
          scanResult: err.response.data,
          scanStatus: SCAN_ERROR,}, () => {
        });
        this.alertRef.current.sendAlert(err.response.data);
        setTimeout(this.cleanMessage, 1500);
      });
    });
  };

  handleScanError = () => {
    this.setState({scanStatus: SCAN_ERROR, scanResult: 'Cannot scan'});
  };

  cleanMessage = () => {
    this.setState({scanStatus: SCAN_SCANNING, scanResult: ''});
  };

  // ---------------------------------------------------------------------------
  // Back order
  // ---------------------------------------------------------------------------
  onBackOrderValueChange = (orderItemId, itemId, value) => {
    const { handleBackOrderChange } = this.props;
    handleBackOrderChange(orderItemId, value);
    this.setState((prevState) => {
      const updatedOrderItems = {
        ...prevState.orderItems
      }
      updatedOrderItems[itemId][orderItemId].in_back_order = value;
      return {
        orderItems: updatedOrderItems,
      }
    })
  };

  onCompletedNotBackOrderValueChange = (orderItemId, itemId, value) => {
    const { handleCompletedNotBackOrderChange } = this.props;
    handleCompletedNotBackOrderChange(orderItemId, value);
    this.setState((prevState) => {
      const updatedOrderItems = {
        ...prevState.orderItems
      }
      updatedOrderItems[itemId][orderItemId].completed_not_back_order = value;
      return {
        orderItems: updatedOrderItems,
      }
    })
  };

  // ---------------------------------------------------------------------------
  // FIFO
  // ---------------------------------------------------------------------------
  handleFifoConfirm = () => {
    const { packageId, inventoryId, format, times } = this.state.pendingFifoPack;
    this.setState({ fifoWarning: null, pendingFifoPack: null, loading: true }, () => {
      addItemToPackage(packageId, inventoryId, format, times, true).then((resp) => {
        const orderItemsForItem = this.state.orderItems[resp.data[0].item.id];
        const orderItemIdList = orderItemsForItem ? Object.keys(orderItemsForItem) : [];
        this.props.onAddItem(packageId, resp.data, orderItemIdList);
        this.setState({ loading: false }, () => {
          const msg = resp.data.length === 1 ? `Inventory #${resp.data[0].id}` : `Inventories`;
          this.alertRef.current.sendAlert(`${msg} successfully added to package ${packageId} (FIFO overridden)`, "success");
        });
        this.updateItemMaps();
      }).catch((error) => {
        this.setState({ loading: false }, () => {
          this.alertRef.current.sendAlert(error.response?.data || 'Error adding item');
        });
      });
    });
  };

  handleFifoCancel = () => {
    this.setState({ fifoWarning: null, pendingFifoPack: null });
  };

  handleFifoError = (error, packageId, inventoryId, format, times) => {
    if (error.response && error.response.status === 409 && error.response.data.fifo_violation) {
      this.setState({
        loading: false,
        fifoWarning: error.response.data,
        pendingFifoPack: { packageId, inventoryId, format, times },
      });
      return true;
    }
    return false;
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  render() {
    const { itemsByStation, itemsAccordionStatus, orderItems } = this.state;
    const { isOrderShipment, errors, openChangeSubstituteItemModal } = this.props;
    const { fifoWarning } = this.state;
    return (
      <div className="card">
        <Alert ref={this.alertRef} />

        {/* FIFO Violation Warning Modal */}
        <Modal
          show={!!fifoWarning}
          onHide={this.handleFifoCancel}
          centered
          backdrop="static"
        >
          <div className="card mb-0">
            <div className="card-header py-2" style={{ backgroundColor: '#ffc107', color: '#34383c' }}>
              <i className="fa fa-exclamation-triangle mr-05"></i>
              <strong>FIFO Violation Warning</strong>
            </div>
            <div className="card-body p-3">
              {fifoWarning && (
                <>
                  <div className="alert alert-warning mb-3">
                    <i className="fa fa-exclamation-triangle mr-05"></i>
                    {fifoWarning.message}
                  </div>
                  {fifoWarning.violations && fifoWarning.violations.map((v, idx) => (
                    <div key={idx} className="card mb-2" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
                      <div className="card-body py-2 px-3">
                        <div className="small">
                          <strong>{v.item}</strong> / {v.brand}
                          <div className="mt-1">
                            Scanned: Inventory #{v.inventory_id} (packed {moment(v.inventory_packing_date).format('MM/DD/YYYY')})
                          </div>
                          <div>
                            Older: <strong>Inventory #{v.older_inventory_id}</strong> (packed {moment(v.older_packing_date).format('MM/DD/YYYY')})
                            {' '}<span className="badge badge-pill badge-warning">{v.days_older} days older</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="d-flex gap-2 mt-3">
                    <button className="btn btn-secondary flex-grow-1" onClick={this.handleFifoCancel}>
                      Cancel
                    </button>
                    <button className="btn btn-warning flex-grow-1" onClick={this.handleFifoConfirm}>
                      <i className="fa fa-exclamation-triangle mr-05"></i>
                      Override FIFO
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </Modal>

        <div className="card-header d-flex justify-content-between align-items-center pointer" onClick={this.toggleCollapse}>
          <div className="text-primary">
            <h6 className="mb-0">
              Package #{this.props.package.id} | {this.props.package.package_type ? this.props.package.package_type.name : 'N/A'}
            </h6>
          </div>
          <div className="d-flex">
            <div onClick={this.onRemovePackage}>
              <i className="btn-danger btn m-0 btn-sm fa fa-trash"></i>
            </div>
            <div className="shipment-package-collapse pointer" onClick={this.toggleCollapse}>
              <i className={`fa ${this.state.collapseOpened ? "fa-caret-up" : "fa-caret-down"}`}></i>
            </div>
          </div>
        </div>
        <Collapse isOpened={this.state.collapseOpened}>
          <div className="card-block">
            {this.state.loading ? <Spinner /> : ""}
            <div>
              <h6>Add inventory items</h6>
              <AddInventoryWidget
                inputVal={this.state.inventoryId}
                onInputChange={this.onItemIdChange}
                onSubmit={this.handleAddInventory}
                onScanItem={this.handleScannedItem}
                onScanError={this.handleScanError}
                scanResult={this.state.scanResult}
                scanStatus={this.state.scanStatus}
                multiple_times_allowed={true}
              />

              {this.props.package.items.length === 0 ? (
                <div className="shipment-no-packages">There are not added items yet</div>
              ) : (
                <div>
                  <h6>Added Inventory Items: {this.props.package.items.length}</h6>
                  {Object.keys(itemsByStation).map((stationName) => {
                    return (
                      <div key={stationName} className="d-flex flex-column">
                        <span className="grouped-table-section-name p-1 pl-2">{stationName}</span>
                        <table className="table table-striped table-bordered table-custom-dark datatable">
                          <thead>
                            <tr className="force-white-bg">
                              <th>Id</th>
                              <th>Item</th>
                              <th className="hidden-xs-down">Inv. Count</th>
                              <th className="hidden-xs-down">Brand</th>
                              <th className="hidden-xs-down">Packing Date</th>
                              <th className="hidden-xs-down">Weight</th>
                              <th className="hidden-xs-down">Pieces</th>
                              {isOrderShipment ? <th>Back Order</th> : <th>Actions</th>}
                            </tr>
                          </thead>
                          {Object.keys(itemsByStation[stationName]).map((itemName) => {
                            const itemInventories = itemsByStation[stationName][itemName];
                            const isItemAccordionOpen = itemsAccordionStatus[itemName];
                            const itemTotalPieces = itemInventories.reduce((prevValue, inv) => {
                              return prevValue + inv.number_items;
                            }, 0);
                            const orderItemsForItem = orderItems[itemInventories[0].item.id];
                            let itemRowHasError = false;
                            if (orderItemsForItem) {
                              itemRowHasError = Object.keys(orderItemsForItem).reduce((prevValue, orderItemId) => {
                                const numId = parseInt(orderItemId, 10);
                                return (
                                  prevValue ||
                                  errors.missingItems.indexOf(numId) !== -1 ||
                                  errors.fulfilledInBackOrder.indexOf(numId) !== -1
                                );
                              }, false);
                            }
                            return (
                              <tbody>
                                <tr className={`${itemRowHasError ? "error-row" : "force-white-bg"}`}>
                                  <td
                                    className="pointer"
                                    onClick={() => {
                                      this.toggleItemRows(itemName);
                                    }}
                                  >
                                    <i className={`fa ${isItemAccordionOpen ? "fa-chevron-up" : "fa-chevron-down"}`} />
                                  </td>
                                  <td>{itemName}</td>
                                  <InventoryCell
                                    orderItems={orderItemsForItem}
                                    invCount={itemInventories.length}
                                    itemInventories={itemInventories}
                                  />
                                  <td className="hidden-xs-down" />
                                  <td className="hidden-xs-down" />
                                  <WeightCell orderItems={orderItemsForItem} itemInventories={itemInventories} />
                                  <td className="hidden-xs-down">{itemTotalPieces}</td>
                                  {isOrderShipment ? (
                                    <BackOrderCell
                                      orderItems={orderItemsForItem}
                                      handleBackOrderChange={this.onBackOrderValueChange}
                                      handleCompletedNotBackOrderChange={this.onCompletedNotBackOrderValueChange}
                                      openChangeSubstituteItemModal={openChangeSubstituteItemModal}
                                      itemInventories={itemInventories}
                                      stationName={stationName}
                                    />
                                  ) : (
                                    <td />
                                  )}
                                </tr>
                                {isItemAccordionOpen &&
                                  itemInventories.map((item) => {
                                    return <PackageItemRow inventory={item} key={item.id} onDelete={this.handleDeleteInventory} />;
                                  })}
                              </tbody>
                            );
                          })}
                        </table>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </Collapse>
      </div>
    );
  }
}
